import React, { useState, useRef } from 'react';
import '../Styles/event_form.css';

// Backend runs on port 5050. When you deploy, change this to your live API URL.
const API_BASE_URL = "https://vanakkamhrd.com";

function EventPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    designation: '',
    company: '',
    contactNumber: '',
    personalEmail: '',
    officialEmail: '',
    howDidYouKnow: '',
  });

  const [fileName, setFileName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setFileName(file ? file.name : '');
  };

  // Loads the Razorpay checkout script once
  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const resetForm = () => {
    setFormData({
      fullName: '',
      designation: '',
      company: '',
      contactNumber: '',
      personalEmail: '',
      officialEmail: '',
      howDidYouKnow: '',
    });
    setFileName('');
    formRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitted(false);

    // Build multipart/form-data so the file upload works alongside the text fields
    const payload = new FormData();
    payload.append('fullName', formData.fullName);
    payload.append('designation', formData.designation);
    payload.append('company', formData.company);
    payload.append('contactNumber', formData.contactNumber);
    payload.append('personalEmail', formData.personalEmail);
    payload.append('officialEmail', formData.officialEmail);
    payload.append('howDidYouKnow', formData.howDidYouKnow);
payload.append('eventName', 'festival');

    const file = fileInputRef.current?.files?.[0];
    if (file) {
      payload.append('image', file);
    }

    try {
      if (!(await loadRazorpay())) {
        throw new Error('Could not load Razorpay. Check your internet.');
      }

      // 1. Save to pending table + create Razorpay order
      const res = await fetch(`${API_BASE_URL}/api/registrations/create-order`, {
        method: 'POST',
        body: payload,
      });
      const order = await res.json();
      if (!res.ok) throw new Error(order.error || 'Could not start payment');

      // 2. Open Razorpay popup
      const rzp = new window.Razorpay({
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        order_id: order.orderId,
        name: 'Vanakkam HRD',
        description: 'Annual HR Summit 2026 Registration',
        prefill: {
          name: order.name,
          email: order.email,
          contact: order.contact,
        },
        theme: { color: '#222664' },

        // 3. After successful payment, verify on the server
        handler: async (response) => {
          try {
            const vr = await fetch(`${API_BASE_URL}/api/registrations/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(response),
            });
            const vj = await vr.json();
            if (!vr.ok) throw new Error(vj.error);

            setSubmitted(true);
            resetForm();
          } catch {
            alert('Payment received but confirmation failed. Please contact support.');
          } finally {
            setSubmitting(false);
          }
        },

        // User closed the popup without paying
        modal: { ondismiss: () => setSubmitting(false) },
      });

      rzp.on('payment.failed', (r) => {
        alert(r.error.description || 'Payment failed. Please try again.');
        setSubmitting(false);
      });

      rzp.open();
    } catch (err) {
      console.error('Submit error:', err);
      alert(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ===== HERO ===== */}
      <section className="event-hero">
        <img
          src="/assets/Eventpage_form_bg.jpeg"
          alt=""
          className="event-hero-bg"
        />
        <div className="event-hero-overlay"></div>

        <img
          src="/assets/AHR-2026-logo.png"
          alt="Annual HR Summit 2026"
          className="event-hero-logo"
        />
      </section>

      {/* ===== REGISTRATION FORM CARD ===== */}
      <section className="event-form-section">
        <div className="event-form-card">
          <h2 className="event-form-heading">Registration Form</h2>

          <form className="event-form" ref={formRef} onSubmit={handleSubmit}>
            <div className="event-form-group">
              <label className="event-form-label">
                Full Name<span className="event-required">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                placeholder="Full name"
                className="event-form-input"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="event-form-group">
              <label className="event-form-label">
                Designation<span className="event-required">*</span>
              </label>
              <input
                type="text"
                name="designation"
                placeholder="Designation"
                className="event-form-input"
                value={formData.designation}
                onChange={handleChange}
                required
              />
            </div>

            <div className="event-form-group">
              <label className="event-form-label">
                Company<span className="event-required">*</span>
              </label>
              <input
                type="text"
                name="company"
                placeholder="Company"
                className="event-form-input"
                value={formData.company}
                onChange={handleChange}
                required
              />
            </div>

            <div className="event-form-group">
              <label className="event-form-label">
                Contact Number<span className="event-required">*</span>
              </label>
              <input
                type="tel"
                name="contactNumber"
                placeholder="Contact Number"
                className="event-form-input"
                value={formData.contactNumber}
                onChange={handleChange}
                required
              />
            </div>

            <div className="event-form-group">
              <label className="event-form-label">
                Personal Email ID<span className="event-required">*</span>
              </label>
              <input
                type="email"
                name="personalEmail"
                placeholder="Email ID"
                className="event-form-input"
                value={formData.personalEmail}
                onChange={handleChange}
                required
              />
            </div>

            <div className="event-form-group">
              <label className="event-form-label">
                Official Email ID<span className="event-required">*</span>
              </label>
              <input
                type="email"
                name="officialEmail"
                placeholder="Email ID"
                className="event-form-input"
                value={formData.officialEmail}
                onChange={handleChange}
                required
              />
            </div>

            <div className="event-form-group">
              <label className="event-form-label">
                How did you know<span className="event-required">*</span>
              </label>
              <select
                name="howDidYouKnow"
                className="event-form-select"
                value={formData.howDidYouKnow}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  -Select-
                </option>
                <option value="social-media">Social Media</option>
                <option value="hr-community">HR Community</option>
                <option value="whatsapp-group">WhatsApp Group</option>
                <option value="google-search">Google Search</option>
                <option value="website">Website</option>
                <option value="friend-colleague">Friends / Colleagues</option>
                <option value="referral">Referral</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="event-form-group">
              <label className="event-form-label">
                Upload Your image<span className="event-required">*</span>{' '}
                <span className="event-form-sublabel">(JPG and PDF)</span>
              </label>

              <div className="event-file-upload">
                <label htmlFor="event-file-input" className="event-file-btn">
                  Choose File
                </label>
                <input
                  id="event-file-input"
                  type="file"
                  accept=".jpg,.jpeg,.pdf"
                  className="event-file-input"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <span className="event-file-name">
                  {fileName || '(Regarding Social Media Poster)'}
                </span>
              </div>
            </div>

            <button type="submit" className="event-submit-btn" disabled={submitting}>
              <span className="event-submit-text">
                {submitting ? 'Please wait...' : 'Submit & Pay'}
              </span>
              <span className="event-submit-arrow">
                <svg
                  width="8"
                  height="12"
                  viewBox="0 0 8 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L6 6L1 11"
                    stroke="#222664"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>

            {submitted && (
              <p className="success-message">✅ Successfully registered!</p>
            )}
          </form>
        </div>
      </section>
    </>
  );
}

export default EventPage;